/**
 * BloomFilter — probabilistic set for packet-ID dedup.
 *
 * Used by the gossip/flooding relay logic to avoid rebroadcasting packets
 * we have already seen. A small false-positive rate is acceptable because
 * the mesh's redundancy compensates for occasional drops.
 *
 * Parameters tuned for ~2048 recent packet IDs with ~1% false-positive rate:
 *   m (bits)  = 20000
 *   k (hashes) = 7
 *
 * The filter is periodically rotated (double-buffered) so old IDs age out
 * instead of saturating the filter forever.
 */

const DEFAULT_BITS   = 20000;
const DEFAULT_HASHES = 7;
const DEFAULT_ROTATE_MS = 10 * 60 * 1000; // 10 minutes

export class BloomFilter {
  constructor({ bits = DEFAULT_BITS, hashes = DEFAULT_HASHES, rotateMs = DEFAULT_ROTATE_MS } = {}) {
    this.bits = bits;
    this.hashes = hashes;
    this.rotateMs = rotateMs;
    this.current = new Uint8Array(Math.ceil(bits / 8));
    this.previous = new Uint8Array(Math.ceil(bits / 8));
    this.lastRotate = Date.now();
  }

  /**
   * Test-and-set: returns true if the item was *probably* already in the set
   * (a true positive or a false positive). Returns false if definitely new —
   * and in that case marks the item as seen.
   */
  seen(bytes) {
    this._maybeRotate();
    let wasSeen = true;
    for (let i = 0; i < this.hashes; i++) {
      const idx = this._hash(bytes, i) % this.bits;
      const byteIdx = idx >>> 3;
      const mask = 1 << (idx & 7);
      const inCurrent = (this.current[byteIdx] & mask) !== 0;
      const inPrevious = (this.previous[byteIdx] & mask) !== 0;
      if (!inCurrent && !inPrevious) {
        wasSeen = false;
      }
      this.current[byteIdx] |= mask;
    }
    return wasSeen;
  }

  _maybeRotate() {
    if (Date.now() - this.lastRotate < this.rotateMs) return;
    this.previous = this.current;
    this.current = new Uint8Array(Math.ceil(this.bits / 8));
    this.lastRotate = Date.now();
  }

  /**
   * Simple MurmurHash3-inspired mixer. Seeded by the hash index so each of
   * the k hashes gives an independent bit position. Good enough for dedup —
   * not cryptographic.
   */
  _hash(bytes, seed) {
    let h = 0xcafebabe ^ (seed * 0x9E3779B1);
    for (let i = 0; i < bytes.length; i++) {
      h ^= bytes[i];
      h = Math.imul(h, 0x5bd1e995);
      h ^= h >>> 15;
    }
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
    return h >>> 0;
  }

  clear() {
    this.current.fill(0);
    this.previous.fill(0);
    this.lastRotate = Date.now();
  }
}

export default BloomFilter;
